import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { KeyRound, Webhook, Globe, ShieldCheck, RefreshCw, Copy, Send, Link2, Unlink, Save, Clock, PlayCircle, ArrowRight, Activity, Sprout, Database, AlertCircle, Download, Filter, HardDrive, Lock, Wrench } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DBGrowthChartWidget } from '@/components/DBGrowthChartWidget';
import { SelectiveRestoreDialog } from '@/components/SelectiveRestoreDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/super-admin`;

const PlatformSettingsPage = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [config, setConfig] = useState(null);
  const [dpo, setDpo] = useState(null);
  const [logs, setLogs] = useState([]);
  const [attempts, setAttempts] = useState(null);
  const [automations, setAutomations] = useState(null);
  const [webhookForm, setWebhookForm] = useState({ url: '', enabled: false, regenerate_secret: false });
  const [dpoForm, setDpoForm] = useState({ dpo_service_email: '', dpo_service_name: '', dpo_service_password: '' });
  const [newKey, setNewKey] = useState(null);
  const [manualKey, setManualKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [dbStatus, setDbStatus] = useState(null);
  const [dbHealth, setDbHealth] = useState(null);
  const [retentionDays, setRetentionDays] = useState('');
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [coverageResult, setCoverageResult] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [backfillTenantId, setBackfillTenantId] = useState('');
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backups, setBackups] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  const [selectiveDialog, setSelectiveDialog] = useState({ open: false, backup: null });
  const [snapshotTenantId, setSnapshotTenantId] = useState('');
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [keyBusy, setKeyBusy] = useState(false);
  const [encryptionConfigured, setEncryptionConfigured] = useState(false);

  const runBackfillSlugs = async () => {
    if (!window.confirm('Regularizar tenants sem slug/database_name? Idempotente.')) return;
    setBackfilling(true);
    try {
      const { data } = await axios.post(`${API}/db/backfill-slugs`, {}, { withCredentials: true });
      toast.success(`Backfill concluído: ${data.fixed_count} tenant(s) atualizado(s)`);
      if (data.fixed_count > 0) {
        console.log('Backfill details:', data.fixed);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no backfill');
    } finally {
      setBackfilling(false);
    }
  };

  const generateBackupKey = async () => {
    if (!window.confirm('Gerar chave de encriptação Fernet? Após criada, novos backups são encriptados.')) return;
    setKeyBusy(true);
    try {
      const { data } = await axios.post(`${API}/db/generate-backup-key`, {}, { withCredentials: true });
      toast.success(`${data.message} (${data.key_preview})`);
      setEncryptionConfigured(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar chave');
    } finally {
      setKeyBusy(false);
    }
  };

  const rotateBackupKey = async () => {
    if (!window.confirm(
      'Rodar a chave de encriptação?\n\nCada backup encriptado será descarregado, decifrado com a chave antiga e re-encriptado com a nova. Se algum backup falhar, a chave atual é mantida.'
    )) return;
    setKeyBusy(true);
    try {
      const { data } = await axios.post(`${API}/db/rotate-backup-key`, { confirm: 'ROTATE' }, { withCredentials: true });
      if (data.success) {
        toast.success(`${data.message}`);
      } else {
        toast.error(`${data.message} - ${data.failed?.length || 0} falha(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rodar chave');
    } finally {
      setKeyBusy(false);
    }
  };

  const downloadBackup = async (backup) => {
    try {
      const response = await axios.get(
        `${API}/db/audit-backups/${backup.backup_id}/download`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/gzip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = (backup.path || backup.backup_id).split('/').pop() || `${backup.backup_id}.json.gz`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup descarregado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao descarregar');
    }
  };

  const backupTenant = async () => {
    if (!snapshotTenantId.trim()) {
      toast.error('Escolha um tenant');
      return;
    }
    setSnapshotBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/db/backup-tenant/${snapshotTenantId.trim()}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Snapshot criado: ${data.docs} docs · ${data.backup?.backup_id || 'sem docs'}`);
      loadBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar snapshot');
    } finally {
      setSnapshotBusy(false);
    }
  };

  const loadBackups = async () => {
    try {
      const { data } = await axios.get(`${API}/db/audit-backups?limit=50`, { withCredentials: true });
      setBackups(data.backups || []);
    } catch (error) {
      // silently ignore - endpoint is optional
    }
  };

  const restoreBackup = async (backup) => {
    if (!window.confirm(`Restaurar ${backup.docs} doc(s) do backup ${backup.backup_id}?`)) return;
    setRestoringId(backup.backup_id);
    try {
      const { data } = await axios.post(
        `${API}/db/audit-backups/restore`,
        { backup_id: backup.backup_id },
        { withCredentials: true }
      );
      toast.success(`Restaurados ${data.restored} · saltados ${data.skipped}`);
      loadDbStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao restaurar');
    } finally {
      setRestoringId(null);
    }
  };

  const loadDbStatus = async () => {
    try {
      const [statusRes, healthRes] = await Promise.all([
        axios.get(`${API}/db/single-db-status`, { withCredentials: true }),
        axios.get(`${API}/db/health`, { withCredentials: true }),
      ]);
      setDbStatus(statusRes.data);
      setDbHealth(healthRes.data);
      const cfg = await axios.get(`${API}/platform-settings`, { withCredentials: true }).catch(() => null);
      if (cfg?.data?.audit_retention_days != null) setRetentionDays(String(cfg.data.audit_retention_days));
      if (cfg?.data?.backup_encryption_configured != null) setEncryptionConfigured(!!cfg.data.backup_encryption_configured);
      await loadBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao ler estado da BD');
    }
  };

  const saveRetention = async () => {
    const value = parseInt(retentionDays, 10);
    if (!Number.isFinite(value) || value < 30) {
      toast.error('Retenção mínima: 30 dias');
      return;
    }
    try {
      await axios.put(
        `${API}/platform-settings`,
        { audit_retention_days: value },
        { withCredentials: true }
      );
      toast.success(`Retenção global definida: ${value} dias`);
      loadDbStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar retenção');
    }
  };

  const runMigrationPreview = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const { data } = await axios.post(`${API}/db/migrate-preview`, {}, { withCredentials: true });
      setMigrationResult({ mode: 'preview', ...data });
      toast.success(`Dry-run OK — ${data.totals.inserted} docs a copiar de ${data.tenants.length} tenants`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no dry-run');
    } finally {
      setMigrating(false);
    }
  };

  const runMigrationApply = async () => {
    const answer = window.prompt(
      'Confirmação de migração\n\nEsta operação copia dados das BDs por-tenant para a master DB (idempotente). Escreva MIGRATE para confirmar:'
    );
    if (answer !== 'MIGRATE') return;
    setMigrating(true);
    setMigrationResult(null);
    try {
      const { data } = await axios.post(`${API}/db/migrate-apply`, { confirm: 'MIGRATE' }, { withCredentials: true });
      setMigrationResult({ mode: 'apply', ...data });
      toast.success(`Migração concluída — ${data.totals.inserted} docs copiados`);
      loadDbStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro na migração');
    } finally {
      setMigrating(false);
    }
  };

  const runCoverageCheck = async () => {
    setCoverageLoading(true);
    setCoverageResult(null);
    try {
      const { data } = await axios.get(`${API}/db/tenant-id-coverage`, { withCredentials: true });
      setCoverageResult(data);
      if (data.totals.orphans === 0) {
        toast.success(`✅ Cobertura completa — ${data.totals.with_tenant_id} docs, 0 órfãos`);
      } else {
        toast.warning(`⚠️ ${data.totals.orphans} documento(s) órfão(s) sem tenant_id`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao verificar cobertura');
    } finally {
      setCoverageLoading(false);
    }
  };

  const runBackfill = async (dryRun) => {
    if (!backfillTenantId) {
      toast.error('Escolha o tenant de destino primeiro');
      return;
    }
    if (!dryRun && !window.confirm(
      `Vai atribuir tenant_id=${backfillTenantId} a TODOS os documentos órfãos no master DB. Confirma?`
    )) return;
    setBackfillBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/db/backfill-tenant-id`,
        { target_tenant_id: backfillTenantId, confirm: 'BACKFILL', dry_run: dryRun },
        { withCredentials: true }
      );
      if (dryRun) {
        const total = (data.changes || []).reduce((s, c) => s + (c.would_update || 0), 0);
        toast.success(`Dry-run: ${total} docs seriam atualizados em ${data.changes.length} coleção(ões)`);
      } else {
        toast.success(`✅ Backfill aplicado — ${data.total_updated} docs atualizados`);
        runCoverageCheck();
      }
      setCoverageResult((prev) => ({ ...prev, _last_backfill: data }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no backfill');
    } finally {
      setBackfillBusy(false);
    }
  };

  const runSmartBackfill = async (dryRun) => {
    if (!dryRun && !window.confirm(
      'Backfill inteligente: vai inferir tenant_id via cross-references (users.tenant_db + user_id). Nenhum tenant_id existente é sobrescrito. Confirma?'
    )) return;
    setBackfillBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/db/backfill-tenant-id-smart`,
        { confirm: 'BACKFILL-SMART', dry_run: dryRun },
        { withCredentials: true }
      );
      const t = data.totals || {};
      if (dryRun) {
        toast.success(`Dry-run inteligente: ${t.updated || 0} atualizações · ${t.unresolved || 0} não resolvidos`);
      } else {
        toast.success(`✅ Smart backfill aplicado — ${t.updated || 0} docs atualizados · ${t.unresolved || 0} não resolvidos`);
        runCoverageCheck();
      }
      setCoverageResult((prev) => ({ ...prev, _last_smart_backfill: data }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no smart backfill');
    } finally {
      setBackfillBusy(false);
    }
  };

  const loadAll = async () => {
    try {
      const [cfg, dpoRes, logRes, attRes, autoRes] = await Promise.all([
        axios.get(`${API}/provisioning-config`, { withCredentials: true }),
        axios.get(`${API}/dpo-service`, { withCredentials: true }),
        axios.get(`${API}/webhook-logs?limit=20`, { withCredentials: true }),
        axios.get(`${API}/login-attempts?limit=25`, { withCredentials: true }),
        axios.get(`${API}/automations`, { withCredentials: true }),
      ]);
      setAutomations(autoRes.data);
      setConfig(cfg.data);
      setWebhookForm({ url: cfg.data.webhook_url || '', enabled: cfg.data.webhook_enabled, regenerate_secret: false });
      setDpo(dpoRes.data);
      setDpoForm({
        dpo_service_email: dpoRes.data.dpo_service_email || '',
        dpo_service_name: dpoRes.data.dpo_service_name || '',
        dpo_service_password: '',
      });
      setLogs(logRes.data.logs || []);
      setAttempts(attRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar definições da plataforma');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('Acesso restrito ao Super Admin');
      navigate('/dashboard', { replace: true });
      return;
    }
    loadAll();
  }, [isSuperAdmin]);

  const rotateKey = async () => {
    if (!window.confirm('Rodar a chave M2M invalida imediatamente a chave atual no SaaS-Hub. Continuar?')) return;
    try {
      const { data } = await axios.post(`${API}/provisioning-key/rotate`, {}, { withCredentials: true });
      setNewKey(data.new_key);
      toast.success('Nova chave gerada - copie-a agora');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rodar chave');
    }
  };

  const setKeyManually = async () => {
    try {
      await axios.post(`${API}/provisioning-key/set`, { key: manualKey }, { withCredentials: true });
      toast.success('Chave importada do Hub');
      setManualKey('');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao definir chave');
    }
  };

  const saveWebhook = async () => {
    try {
      const { data } = await axios.put(`${API}/provisioning-webhook`, webhookForm, { withCredentials: true });
      if (data.signing_secret) setNewKey(data.signing_secret);
      toast.success('Webhook do Hub guardado');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar webhook');
    }
  };

  const testWebhook = async () => {
    try {
      const { data } = await axios.post(`${API}/webhook-test`, {}, { withCredentials: true });
      data.success ? toast.success(`Entregue (HTTP ${data.status_code})`) : toast.error(`Falhou: ${data.error || data.status_code}`);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no teste');
    }
  };

  const saveDpo = async () => {
    try {
      const payload = { ...dpoForm };
      if (!payload.dpo_service_password) delete payload.dpo_service_password;
      await axios.put(`${API}/platform-settings`, payload, { withCredentials: true });
      toast.success('DPO as a Service atualizado');
      setDpoForm((f) => ({ ...f, dpo_service_password: '' }));
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar');
    }
  };

  const toggleTenantLink = async (tenantId, linked) => {
    try {
      if (linked) {
        await axios.delete(`${API}/dpo-service/tenants/${tenantId}`, { withCredentials: true });
        toast.success('Organização desligada do serviço');
      } else {
        await axios.post(`${API}/dpo-service/tenants/${tenantId}`, {}, { withCredentials: true });
        toast.success('Organização ligada ao serviço');
      }
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar ligação');
    }
  };

  const seedMemberships = async () => {
    if (!window.confirm(
      'Vai recriar as associações do DPO as a Service com base no modo de cada organização (recomendado após primeiro deploy). Continuar?'
    )) return;
    try {
      const { data } = await axios.post(`${API}/dpo-service/seed`, {}, { withCredentials: true });
      toast.success(`${data.seeded_count} membership(s) semeada(s)`);
      if (data.shadow_failed?.length) {
        toast.warning(`Shadow em falha: ${data.shadow_failed.join(', ')}`);
      }
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao semear memberships');
    }
  };

  const toggleAutomation = async (key, value) => {
    try {
      await axios.put(`${API}/automations`, { [key]: value }, { withCredentials: true });
      toast.success(value ? 'Automação ativada' : 'Automação desativada');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar automação');
    }
  };

  const runAutomation = async (job) => {
    try {
      const { data } = await axios.post(`${API}/automations/${job}/run-now`, {}, { withCredentials: true });
      toast.success(`Executado: ${JSON.stringify(data.result)}`);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao executar');
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  const linkedIds = (dpo?.linked_tenants || []).map((t) => t.tenant_id);

  return (
    <div className="space-y-8" data-testid="platform-settings-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Definições da Plataforma
        </h1>
        <p className="text-base text-slate-500 mt-1">Integração M2M com o SaaS-Hub, DPO as a Service e segurança</p>
      </div>

      {newKey && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="p-5 flex items-start gap-4">
            <KeyRound className="w-5 h-5 text-amber-600 mt-1" />
            <div className="flex-1">
              <p className="font-medium text-amber-900 dark:text-amber-200">Guarde este segredo agora - não voltará a ser mostrado</p>
              <code className="block mt-2 p-3 bg-white dark:bg-slate-900 rounded text-xs break-all" data-testid="new-secret-value">{newKey}</code>
            </div>
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(newKey); toast.success('Copiado'); }} data-testid="copy-secret-button">
              <Copy className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="provisioning">
        <TabsList>
          <TabsTrigger value="provisioning" data-testid="tab-provisioning">Provisioning (SaaS-Hub)</TabsTrigger>
          <TabsTrigger value="dpo" data-testid="tab-dpo-service">DPO as a Service</TabsTrigger>
          <TabsTrigger value="automations" data-testid="tab-automations">Automações</TabsTrigger>
          <TabsTrigger value="database" data-testid="tab-database">Base de Dados</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Segurança</TabsTrigger>
        </TabsList>

        <TabsContent value="provisioning" className="space-y-6 mt-6">
          <Card className={config?.hub_health?.healthy === false ? 'border-red-400 bg-red-50 dark:bg-red-950/20' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Activity className="w-5 h-5" /> Estado da ligação ao Hub
              </CardTitle>
              <CardDescription>
                Alerta automático ao Super Admin após {config?.hub_health?.threshold || 3} entregas falhadas consecutivas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <Badge
                  variant={config?.hub_health?.healthy === false ? 'destructive' : 'default'}
                  data-testid="hub-health-status"
                >
                  {config?.hub_health?.healthy === false ? 'Hub em falha' : 'Ligação saudável'}
                </Badge>
                <span className="text-sm text-slate-500">
                  {config?.hub_health?.consecutive_failures || 0} falha(s) consecutiva(s)
                  {config?.hub_health?.failing_since &&
                    ` · desde ${new Date(config.hub_health.failing_since).toLocaleString('pt-PT')}`}
                </span>
              </div>
              {config?.hub_health?.last_reason && config?.hub_health?.healthy === false && (
                <p className="text-sm text-red-600 dark:text-red-400" data-testid="hub-health-reason">
                  Último motivo: {config.hub_health.last_reason}
                </p>
              )}
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={config?.webhook_failure_alerts_enabled !== false}
                  onCheckedChange={(v) => toggleAutomation('webhook_failure_alerts_enabled', v)}
                  data-testid="hub-failure-alerts-switch"
                />
                <Label>Avisar por email quando o Hub falhar</Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><KeyRound className="w-5 h-5" /> Chave M2M (X-API-Key)</CardTitle>
              <CardDescription>Usada pelo SaaS-Hub em todos os pedidos a /api/provisioning/*</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center gap-3">
                <Badge variant={config?.configured ? 'default' : 'destructive'} data-testid="provisioning-key-status">
                  {config?.configured ? 'Configurada' : 'Não configurada'}
                </Badge>
                {config?.key_preview && <code className="text-xs text-slate-500">{config.key_preview}</code>}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={rotateKey} data-testid="rotate-key-button">
                  <RefreshCw className="w-4 h-4 mr-2" /> Rodar chave
                </Button>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                <Input
                  placeholder="Importar chave definida no Hub (mín. 32 caracteres)"
                  value={manualKey}
                  onChange={(e) => setManualKey(e.target.value)}
                  data-testid="manual-key-input"
                />
                <Button variant="outline" onClick={setKeyManually} disabled={manualKey.length < 32} data-testid="set-key-button">
                  Definir
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><Webhook className="w-5 h-5" /> Webhook Privaxia → Hub</CardTitle>
              <CardDescription>Eventos assinados com HMAC-SHA256: tenant.created, tenant.suspended, tenant.plan_changed, tenant.limit_reached</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label>URL do endpoint no Hub</Label>
                <Input
                  placeholder="https://hub.cogniseg.com/webhooks/privaxia"
                  value={webhookForm.url}
                  onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                  data-testid="webhook-url-input"
                />
              </div>
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={webhookForm.enabled}
                    onCheckedChange={(v) => setWebhookForm({ ...webhookForm, enabled: v })}
                    data-testid="webhook-enabled-switch"
                  />
                  <Label>Ativo</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={webhookForm.regenerate_secret}
                    onCheckedChange={(v) => setWebhookForm({ ...webhookForm, regenerate_secret: v })}
                    data-testid="webhook-regenerate-switch"
                  />
                  <Label>Gerar novo signing secret</Label>
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={saveWebhook} data-testid="save-webhook-button"><Save className="w-4 h-4 mr-2" /> Guardar</Button>
                <Button variant="outline" onClick={testWebhook} data-testid="test-webhook-button"><Send className="w-4 h-4 mr-2" /> Enviar teste</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Entregas recentes</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {logs.length === 0 ? (
                <p className="px-6 py-8 text-center text-slate-500">Sem entregas registadas.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="webhook-logs-table">
                    <thead className="text-slate-500 border-y border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="text-left px-6 py-3 font-medium">Evento</th>
                        <th className="text-left px-6 py-3 font-medium">Origem → Destino</th>
                        <th className="text-left px-6 py-3 font-medium">Resultado</th>
                        <th className="text-left px-6 py-3 font-medium">Quando</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.log_id} className="border-b border-slate-100 dark:border-slate-800 align-top">
                          <td className="px-6 py-3">
                            <p className="font-medium" data-testid={`webhook-event-label-${log.log_id}`}>
                              {log.event_label || log.event}
                            </p>
                            <code className="text-xs text-slate-500">{log.event}</code>
                          </td>
                          <td className="px-6 py-3" data-testid={`webhook-route-${log.log_id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{log.origin || 'Privaxia'}</span>
                              <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                              <span className="font-medium">{log.destination_host || '—'}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {log.origin_detail || 'Plataforma Privaxia'}
                              {log.destination ? ` · ${log.destination}` : ''}
                            </p>
                          </td>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-2">
                              <Badge variant={log.success ? 'default' : 'destructive'}>
                                {log.success ? 'Entregue' : 'Falhou'}
                              </Badge>
                              <span className="text-xs text-slate-500">{log.status_code || 'sem resposta'}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1" data-testid={`webhook-status-label-${log.log_id}`}>
                              {log.status_label}
                            </p>
                            {log.error && <p className="text-xs text-red-500 mt-1">{log.error}</p>}
                          </td>
                          <td className="px-6 py-3 text-slate-500 whitespace-nowrap">
                            {log.attempted_at ? new Date(log.attempted_at).toLocaleString('pt-PT') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dpo" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><Globe className="w-5 h-5" /> Utilizador global</CardTitle>
              <CardDescription>Um único utilizador cross-tenant que presta o serviço de DPO às organizações ligadas</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center gap-3">
                <Badge variant={dpo?.has_password ? 'default' : 'destructive'} data-testid="dpo-password-status">
                  {dpo?.has_password ? 'Password definida' : 'Password não definida'}
                </Badge>
                {dpo?.global_user && <span className="text-sm text-slate-500">{dpo.global_user.email}</span>}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Email do serviço</Label>
                  <Input
                    value={dpoForm.dpo_service_email}
                    onChange={(e) => setDpoForm({ ...dpoForm, dpo_service_email: e.target.value })}
                    data-testid="dpo-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nome do serviço</Label>
                  <Input
                    value={dpoForm.dpo_service_name}
                    onChange={(e) => setDpoForm({ ...dpoForm, dpo_service_name: e.target.value })}
                    data-testid="dpo-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nova password (mín. 8 caracteres)</Label>
                  <Input
                    type="password"
                    placeholder="Deixe vazio para manter"
                    value={dpoForm.dpo_service_password}
                    onChange={(e) => setDpoForm({ ...dpoForm, dpo_service_password: e.target.value })}
                    data-testid="dpo-password-input"
                  />
                </div>
              </div>
              <Button onClick={saveDpo} data-testid="save-dpo-button"><Save className="w-4 h-4 mr-2" /> Guardar</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">Organizações servidas</CardTitle>
                  <CardDescription>Ligar uma organização cria a associação e inclui o serviço nas notificações operacionais</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={seedMemberships} data-testid="seed-dpo-memberships-button">
                  <Sprout className="w-4 h-4 mr-2" /> Semear memberships
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="dpo-tenants-table">
                  <thead className="text-slate-500 border-y border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="text-left px-6 py-3 font-medium">Organização</th>
                      <th className="text-left px-6 py-3 font-medium">Slug</th>
                      <th className="text-left px-6 py-3 font-medium">Modo</th>
                      <th className="text-right px-6 py-3 font-medium">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dpo?.all_tenants || []).map((t) => {
                      const linked = linkedIds.includes(t.tenant_id);
                      return (
                        <tr key={t.tenant_id} className="border-b border-slate-100 dark:border-slate-800">
                          <td className="px-6 py-3 font-medium">{t.name}</td>
                          <td className="px-6 py-3 text-slate-500">{t.slug || '—'}</td>
                          <td className="px-6 py-3">
                            <Badge variant={linked ? 'default' : 'outline'}>{linked ? 'DPO as a Service' : 'DPO do cliente'}</Badge>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <Button
                              size="sm"
                              variant={linked ? 'outline' : 'default'}
                              onClick={() => toggleTenantLink(t.tenant_id, linked)}
                              data-testid={`toggle-dpo-${t.slug || t.tenant_id}`}
                            >
                              {linked ? <><Unlink className="w-4 h-4 mr-1" /> Desligar</> : <><Link2 className="w-4 h-4 mr-1" /> Ligar</>}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="automations" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><Clock className="w-5 h-5" /> Emails automáticos</CardTitle>
              <CardDescription>Agendadores internos da aplicação (sobrevivem a redeploys, ao contrário do crontab)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start justify-between gap-6 pb-6 border-b border-slate-200 dark:border-slate-700">
                <div>
                  <p className="font-medium">Avisos de quota do plano</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Email aos admins da organização aos 90% e aos 100% dos limites (utilizadores, titulares, pedidos/mês). Máximo 1 email por nível e por mês.
                  </p>
                </div>
                <Switch
                  checked={!!automations?.quota_alerts_enabled}
                  onCheckedChange={(v) => toggleAutomation('quota_alerts_enabled', v)}
                  data-testid="quota-alerts-switch"
                />
              </div>

              {(automations?.jobs || []).map((job) => (
                <div key={job.job} className="flex items-start justify-between gap-6 pb-6 border-b border-slate-200 dark:border-slate-700 last:border-0 last:pb-0">
                  <div>
                    <p className="font-medium">{job.label}</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Última execução:{' '}
                      {job.last_run ? new Date(job.last_run).toLocaleString('pt-PT') : 'nunca'}
                      {job.last_result && ` · ${JSON.stringify(job.last_result)}`}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3"
                      onClick={() => runAutomation(job.job)}
                      data-testid={`run-${job.job}-button`}
                    >
                      <PlayCircle className="w-4 h-4 mr-2" /> Executar agora
                    </Button>
                  </div>
                  <Switch
                    checked={job.enabled}
                    onCheckedChange={(v) => toggleAutomation(`${job.job}_enabled`, v)}
                    data-testid={`${job.job}-switch`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="database" className="space-y-6 mt-6" data-testid="database-tab">
          <Card data-testid="db-operations-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-slate-700" />
                Operações da plataforma
              </CardTitle>
              <CardDescription>Backfills e encriptação de backups. Ações idempotentes e seguras.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border border-slate-200 p-3 space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold">Regularizar tenants sem slug</p>
                    <p className="text-xs text-slate-500 max-w-xl">
                      Corrige tenants legados sem <code>slug</code> ou <code>database_name</code> - necessário para
                      compatibilidade com o SaaS-Hub. Idempotente.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={runBackfillSlugs}
                    disabled={backfilling}
                    data-testid="run-backfill-slugs-button"
                  >
                    <Sprout className="w-4 h-4 mr-2" />
                    {backfilling ? 'A correr...' : 'Correr backfill'}
                  </Button>
                </div>
              </div>

              <div className="rounded-md border border-slate-200 p-3 space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold flex items-center gap-2">
                      <Lock className="w-4 h-4" /> Encriptação de backups (Fernet)
                    </p>
                    <p className="text-xs text-slate-500 max-w-xl">
                      Estado:{' '}
                      {encryptionConfigured ? (
                        <span className="text-emerald-700 font-semibold" data-testid="encryption-status-enabled">
                          ✓ Ativa - novos backups são encriptados
                        </span>
                      ) : (
                        <span className="text-amber-700 font-semibold" data-testid="encryption-status-disabled">
                          ⚫ Desativa - backups em plaintext gzip
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!encryptionConfigured ? (
                      <Button
                        onClick={generateBackupKey}
                        disabled={keyBusy}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid="generate-backup-key-button"
                      >
                        <KeyRound className="w-4 h-4 mr-2" />
                        {keyBusy ? '...' : 'Gerar chave'}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={rotateBackupKey}
                        disabled={keyBusy}
                        data-testid="rotate-backup-key-button"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {keyBusy ? '...' : 'Rodar chave'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <DBGrowthChartWidget />

          <Card data-testid="db-health-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-600" />
                Saúde da base de dados
              </CardTitle>
              <CardDescription>Tamanho, contagens e recomendações de purga em tempo real.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={loadDbStatus}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
                </Button>
              </div>

              {dbHealth && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
                      <p className="text-xs text-slate-500">Tamanho dos dados</p>
                      <p className="text-lg font-semibold">{(dbHealth.data_size_bytes / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
                      <p className="text-xs text-slate-500">Índices</p>
                      <p className="text-lg font-semibold">{(dbHealth.index_size_bytes / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
                      <p className="text-xs text-slate-500">Documentos</p>
                      <p className="text-lg font-semibold">{dbHealth.objects.toLocaleString('pt-PT')}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
                      <p className="text-xs text-slate-500">Coleções</p>
                      <p className="text-lg font-semibold">{dbHealth.collections_total}</p>
                    </div>
                  </div>

                  {dbHealth.forecast && (
                    <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm" data-testid="db-forecast-panel">
                      <p className="font-semibold text-indigo-900 mb-1">Previsão de crescimento ({dbHealth.forecast.samples} snapshots · {dbHealth.forecast.span_days}d)</p>
                      <p className="text-indigo-800">Crescimento diário: <strong>{(dbHealth.forecast.daily_growth_bytes / 1024).toFixed(2)} KB/dia</strong></p>
                      <p className="text-indigo-800">Projeção 90d: <strong>{(dbHealth.forecast.projection_90d_bytes / 1024 / 1024).toFixed(2)} MB</strong></p>
                      <p className="text-indigo-800">Projeção 12m: <strong>{(dbHealth.forecast.projection_365d_bytes / 1024 / 1024).toFixed(2)} MB</strong></p>
                      {dbHealth.forecast.note && <p className="text-amber-700 mt-1"><AlertCircle className="w-3 h-3 inline mr-1" />{dbHealth.forecast.note}</p>}
                    </div>
                  )}

                  {dbHealth.purge_suggestion && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="purge-suggestion">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{dbHealth.purge_suggestion}</span>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-semibold mb-2">Top 5 coleções por tamanho</p>
                    <table className="w-full text-sm border border-slate-200 rounded-md overflow-hidden">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left p-2">Coleção</th>
                          <th className="text-right p-2">Docs</th>
                          <th className="text-right p-2">Tamanho</th>
                          <th className="text-right p-2">Índices</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dbHealth.top_by_size.map((c) => (
                          <tr key={c.name} className="border-t border-slate-200" data-testid={`top-col-${c.name}`}>
                            <td className="p-2 font-mono text-xs">{c.name}</td>
                            <td className="p-2 text-right">{(c.count || 0).toLocaleString('pt-PT')}</td>
                            <td className="p-2 text-right">{((c.size_bytes || 0) / 1024).toFixed(1)} KB</td>
                            <td className="p-2 text-right">{c.indexes || 0} ({((c.index_size_bytes || 0) / 1024).toFixed(1)} KB)</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="rounded-md border border-slate-200 p-3 flex flex-wrap items-end gap-3">
                    <div>
                      <Label className="text-xs">Retenção global de audit_logs (dias)</Label>
                      <Input
                        type="number"
                        min="30"
                        value={retentionDays}
                        onChange={(e) => setRetentionDays(e.target.value)}
                        placeholder="1825"
                        className="w-40"
                        data-testid="retention-days-input"
                      />
                    </div>
                    <Button onClick={saveRetention} data-testid="save-retention-button">
                      <Save className="w-4 h-4 mr-2" /> Guardar retenção
                    </Button>
                    <p className="text-xs text-slate-500 max-w-md">
                      Mínimo 30 dias. Tenants em setores regulados podem ter override na gestão do tenant.
                      A próxima purga usa este valor + backup em Object Storage.
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card data-testid="backups-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-indigo-600" />
                Backups de audit_logs
              </CardTitle>
              <CardDescription>
                Snapshots imutáveis (JSON.gz) criados antes de cada purga. Ficam retidos 30 dias e podem ser restaurados.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={loadBackups} data-testid="backups-refresh">
                  <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
                </Button>
              </div>
              {backups.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">Sem backups guardados.</p>
              ) : (
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-72 overflow-y-auto">
                  {backups.map((b) => (
                    <div key={b.backup_id} className="p-3 flex items-center gap-3" data-testid={`backup-${b.backup_id}`}>
                      <Database className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {b.scope} · {b.docs} docs · {((b.bytes || 0) / 1024).toFixed(1)} KB
                        </p>
                        <p className="text-xs text-slate-500 truncate">{b.path}</p>
                        <p className="text-xs text-slate-400">
                          criado {new Date(b.created_at).toLocaleString('pt-PT')} · expira {b.expires_at ? new Date(b.expires_at).toLocaleDateString('pt-PT') : '—'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadBackup(b)}
                        data-testid={`download-${b.backup_id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectiveDialog({ open: true, backup: b })}
                        title="Restore com filtros (data, ação, limite)"
                        data-testid={`selective-restore-${b.backup_id}`}
                      >
                        <Filter className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={restoringId === b.backup_id}
                        onClick={() => restoreBackup(b)}
                        data-testid={`restore-${b.backup_id}`}
                      >
                        {restoringId === b.backup_id ? '...' : 'Restaurar'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="rounded-md border border-slate-200 p-3 space-y-2">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <HardDrive className="w-4 h-4" /> Snapshot on-demand por tenant
                </p>
                <p className="text-xs text-slate-500">
                  Cria imediatamente um backup dos audit_logs de um tenant. Recomendado antes de uma purga manual crítica.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[220px]">
                    <Label className="text-xs">Tenant ID</Label>
                    <Input
                      placeholder="tenant_..."
                      value={snapshotTenantId}
                      onChange={(e) => setSnapshotTenantId(e.target.value)}
                      data-testid="snapshot-tenant-id-input"
                    />
                  </div>
                  <Button
                    onClick={backupTenant}
                    disabled={snapshotBusy}
                    data-testid="snapshot-tenant-button"
                  >
                    <HardDrive className="w-4 h-4 mr-2" />
                    {snapshotBusy ? 'A criar...' : 'Criar snapshot'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-slate-700" />
                Colapsar arquitetura multi-DB para DB única
              </CardTitle>
              <CardDescription>
                A app suporta duas arquiteturas: (a) uma BD física por tenant (legado, requer permissões que o Atlas
                gerido não concede) e (b) DB única com <code>tenant_id</code> como discriminador. Este ecrã copia os
                dados existentes das BDs por-tenant para a master DB (idempotente) e reporta o resultado. Depois de
                aplicada, ative o modo com <code>PRIVAXIA_SINGLE_DB=true</code> no <code>backend/.env</code>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-3 items-center">
                <Button variant="outline" onClick={loadDbStatus} data-testid="db-status-refresh">
                  <RefreshCw className="w-4 h-4 mr-2" /> Ler estado
                </Button>
                <Button
                  variant="outline"
                  onClick={runMigrationPreview}
                  disabled={migrating}
                  data-testid="db-migrate-preview-button"
                >
                  <PlayCircle className="w-4 h-4 mr-2" /> Simular migração (dry-run)
                </Button>
                <Button
                  onClick={runMigrationApply}
                  disabled={migrating}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="db-migrate-apply-button"
                >
                  <Database className="w-4 h-4 mr-2" /> Aplicar migração
                </Button>
              </div>

              {dbStatus && (
                <div className="rounded-md border border-slate-200 p-4 bg-slate-50 text-sm space-y-1" data-testid="db-status-panel">
                  <p><strong>Modo BD única (PRIVAXIA_SINGLE_DB):</strong> {dbStatus.single_db_mode ? '🟢 ATIVO' : '⚫ inativo'}</p>
                  <p><strong>Master DB:</strong> <code>{dbStatus.master_db}</code></p>
                  <p><strong>Tenants configurados:</strong> {dbStatus.tenants_configured}</p>
                  <p><strong>Contagens master:</strong></p>
                  <ul className="ml-4 list-disc">
                    {Object.entries(dbStatus.master_counts).map(([k, v]) => (
                      <li key={k}><code>{k}</code>: {typeof v === 'number' ? v.toLocaleString('pt-PT') : v}</li>
                    ))}
                  </ul>
                </div>
              )}

              {migrationResult && (
                <div
                  className={`rounded-md border p-4 text-sm ${migrationResult.applied ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 bg-slate-50'}`}
                  data-testid="db-migration-result"
                >
                  <p className="font-semibold mb-2">
                    {migrationResult.applied ? '✅ Migração aplicada' : '👀 Simulação (dry-run)'} — Totais: seen={migrationResult.totals.seen} · inseridos={migrationResult.totals.inserted} · saltados={migrationResult.totals.skipped}
                  </p>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {migrationResult.tenants.map((t, i) => (
                      <div key={i} className="border-t border-slate-200 pt-2">
                        <p className="font-medium">{t.tenant || '—'} <span className="text-slate-500">({t.slug || 'sem slug'})</span></p>
                        {t.error ? (
                          <p className="text-red-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {t.error}</p>
                        ) : (
                          <ul className="ml-4 list-disc text-xs">
                            {Object.entries(t.collections || {}).map(([c, s]) => (
                              <li key={c}><code>{c}</code>: seen={s.seen} inserted={s.inserted} skipped={s.skipped}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <p className="font-semibold mb-1">Passo-a-passo recomendado</p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Clique <strong>Simular migração</strong> e confira as contagens.</li>
                  <li>Clique <strong>Aplicar migração</strong> (pode correr várias vezes — é idempotente).</li>
                  <li><strong>Verifique cobertura tenant_id</strong> abaixo — se houver órfãos, faça <strong>backfill</strong>.</li>
                  <li>Peça ao operador para adicionar <code>PRIVAXIA_SINGLE_DB=true</code> em produção e fazer restart do backend.</li>
                  <li>Valide <code>/tenants</code> e um dashboard por tenant. As BDs legadas podem ser eliminadas no Atlas manualmente depois.</li>
                </ol>
              </div>

              {/* Cobertura tenant_id + Backfill */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-slate-800">Cobertura tenant_id (master DB)</p>
                    <p className="text-xs text-slate-500">
                      Verifica se cada documento tem <code>tenant_id</code>. Se não tiver, ficará invisível no modo Single-DB.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={runCoverageCheck}
                    disabled={coverageLoading}
                    data-testid="db-coverage-check-button"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${coverageLoading ? 'animate-spin' : ''}`} />
                    Verificar cobertura
                  </Button>
                </div>

                {coverageResult && (
                  <div
                    className={`rounded-md border p-3 text-sm space-y-3 ${
                      coverageResult.totals.orphans === 0 ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'
                    }`}
                    data-testid="db-coverage-result"
                  >
                    <div className="flex flex-wrap gap-4">
                      <span><strong>Total:</strong> {coverageResult.totals.total.toLocaleString('pt-PT')}</span>
                      <span><strong>Com tenant_id:</strong> {coverageResult.totals.with_tenant_id.toLocaleString('pt-PT')}</span>
                      <span className={coverageResult.totals.orphans > 0 ? 'text-amber-800 font-semibold' : ''}>
                        <strong>Órfãos:</strong> {coverageResult.totals.orphans.toLocaleString('pt-PT')}
                      </span>
                    </div>
                    <p className="text-xs italic text-slate-700">{coverageResult.recommendation}</p>

                    <div className="max-h-56 overflow-y-auto border rounded bg-white/70">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1">Coleção</th>
                            <th className="text-right px-2 py-1">Total</th>
                            <th className="text-right px-2 py-1">Com tid</th>
                            <th className="text-right px-2 py-1">Órfãos</th>
                            <th className="text-right px-2 py-1">Cobertura</th>
                            <th className="text-left px-2 py-1">Risco</th>
                          </tr>
                        </thead>
                        <tbody>
                          {coverageResult.collections.map((c) => (
                            <tr key={c.collection} className="border-t border-slate-100">
                              <td className="px-2 py-1 font-mono">{c.collection}</td>
                              {c.error ? (
                                <td colSpan={5} className="px-2 py-1 text-red-600">{c.error}</td>
                              ) : (
                                <>
                                  <td className="px-2 py-1 text-right">{c.total}</td>
                                  <td className="px-2 py-1 text-right">{c.with_tenant_id}</td>
                                  <td className={`px-2 py-1 text-right ${c.orphans_without_tenant_id > 0 ? 'font-semibold text-amber-700' : ''}`}>
                                    {c.orphans_without_tenant_id}
                                  </td>
                                  <td className="px-2 py-1 text-right">{c.coverage_pct}%</td>
                                  <td className="px-2 py-1">
                                    {c.risk === 'ok' ? '🟢 ok' : c.risk === 'safe' ? '⚪ vazio' : c.risk === 'orphans' ? '🟠 órfãos' : '🔴 tenant desconhecido'}
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {coverageResult.totals.orphans > 0 && (
                      <div className="border-t pt-3 space-y-3">
                        <div className="rounded border border-emerald-300 bg-emerald-50 p-3 space-y-2">
                          <p className="text-xs font-semibold text-emerald-900">
                            🧠 Smart Backfill (recomendado)
                          </p>
                          <p className="text-xs text-emerald-800">
                            Infere <code>tenant_id</code> automaticamente via cross-references:
                            <br />• <strong>users</strong>: via campo <code>tenant_db</code> → mapa <code>tenants.database_name</code>
                            <br />• <strong>consents/documents/etc</strong>: via <code>user_id</code> → tenant_id do user
                            <br />Não sobrescreve <code>tenant_id</code> existente. Seguro para multi-tenant.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => runSmartBackfill(true)}
                              disabled={backfillBusy}
                              data-testid="smart-backfill-dry-run-button"
                            >
                              <PlayCircle className="w-3 h-3 mr-1" /> Simular smart backfill
                            </Button>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => runSmartBackfill(false)}
                              disabled={backfillBusy}
                              data-testid="smart-backfill-apply-button"
                            >
                              <Database className="w-3 h-3 mr-1" /> Aplicar smart backfill
                            </Button>
                          </div>
                        </div>

                        <details className="text-xs">
                          <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                            ⚙️ Backfill manual (avançado) — atribuir tudo a 1 tenant
                          </summary>
                          <div className="mt-2 space-y-2 border rounded p-2 bg-white">
                            <p className="text-xs text-slate-600">
                              ⚠️ Use apenas se souber que TODOS os órfãos pertencem a um único tenant.
                              Todos os documentos sem <code>tenant_id</code> receberão o valor escolhido.
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                              <select
                                className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
                                value={backfillTenantId}
                                onChange={(e) => setBackfillTenantId(e.target.value)}
                                data-testid="backfill-tenant-select"
                              >
                                <option value="">-- Escolher tenant destino --</option>
                                {(coverageResult.known_tenants || []).map((t) => (
                                  <option key={t.tenant_id} value={t.tenant_id}>
                                    {t.name} ({t.slug || 'sem slug'}) — {t.tenant_id}
                                  </option>
                                ))}
                              </select>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => runBackfill(true)}
                                disabled={backfillBusy || !backfillTenantId}
                                data-testid="backfill-dry-run-button"
                              >
                                <PlayCircle className="w-3 h-3 mr-1" /> Simular
                              </Button>
                              <Button
                                size="sm"
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                onClick={() => runBackfill(false)}
                                disabled={backfillBusy || !backfillTenantId}
                                data-testid="backfill-apply-button"
                              >
                                <Database className="w-3 h-3 mr-1" /> Aplicar backfill manual
                              </Button>
                            </div>
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="w-5 h-5" /> Tentativas de login</CardTitle>
              <CardDescription>Bloqueio automático após 5 falhas por email ou IP em 15 minutos</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold mb-6" data-testid="failed-logins-24h">{attempts?.failed_last_24h ?? 0}
                <span className="text-sm font-normal text-slate-500 ml-2">falhas nas últimas 24h</span>
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="login-attempts-table">
                  <thead className="text-slate-500 border-y border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">Email</th>
                      <th className="text-left px-4 py-3 font-medium">Resultado</th>
                      <th className="text-left px-4 py-3 font-medium">IP (hash)</th>
                      <th className="text-left px-4 py-3 font-medium">Quando</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(attempts?.attempts || []).map((a, i) => (
                      <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="px-4 py-3">{a.email}</td>
                        <td className="px-4 py-3">
                          <Badge variant={a.success ? 'default' : 'destructive'}>{a.success ? 'Sucesso' : 'Falha'}</Badge>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{(a.ip_hash || '').slice(0, 12)}…</td>
                        <td className="px-4 py-3 text-slate-500">{a.created_at ? new Date(a.created_at).toLocaleString('pt-PT') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {loading && <p className="text-slate-500">A carregar...</p>}

      <SelectiveRestoreDialog
        open={selectiveDialog.open}
        onOpenChange={(open) => setSelectiveDialog({ open, backup: selectiveDialog.backup })}
        backup={selectiveDialog.backup}
        onRestored={loadDbStatus}
      />
    </div>
  );
};

export default PlatformSettingsPage;
